import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Grid,
  Header,
  Segment,
  Card,
  Icon,
  Statistic,
  Image,
  Button,
  Divider,
  Table,
  Label,
  Input,
  Dropdown,
  Modal,
  Message,
  List,
  Checkbox,
  Pagination
} from 'semantic-ui-react';
import { AuthStdFooter } from "../../utils/auth-std-footer";
// import { getMenuIdForFeature } from '../../utils/roleUtils';
import useRoleNormalization from '../../hooks/useRoleNormalization';
import '../../styles/landing-pages.css';
import '../../styles/article.css';
import userPuzzle from '../../assets/userPuzzle.svg';
import voiceActressBW from '../../assets/micAndshield.svg';
import bookStack from '../../assets/justBookStack.svg';
import lightening from '../../assets/lightening.svg';
// import checkBox from '../../assets/checkListLine.svg';
import robotGearRocket from '../../assets/robotGearRocket.svg';
import warningIcon from '../../assets/info_icon_red.svg';
import activeUsersIcon from '../../assets/active-users.svg';

// Import RealUserService for fetching user data
import realUserService from '../../components/messaging/services/RealUserService';
// Import Database Audit component
import DatabaseAudit from './DatabaseAudit';
import VoiceSlotManager from './VoiceSlotManager';
import LoginAllowlistManager from './LoginAllowlistManager';

import apiClient from '../../apiTools/apiClient';
import {
  fetchDbHealth, fetchDbStatistics, fetchResetPreview, fetchDeletionAudit, fetchS3Reconcile, reclaimS3,
  selectDbHealth, selectDbStatistics, selectResetPreview, selectDeletionAudit, selectS3Reconcile, selectS3Reclaim,
} from '../../slices/databaseAuditSlice';
import {
  FaDatabase, FaUsers, FaCloud, FaTrashAlt, FaSync, FaExclamationTriangle,
  FaShieldAlt, FaHeartbeat, FaCheckCircle, FaTimesCircle
} from 'react-icons/fa';

const UserManagement = () => {
  const userProfile = useSelector((state) => state.user.profile);
  const userName = userProfile?.data?.title || 'Administrator';
  const navigate = useNavigate();

  // Initialize role normalization hook
  const {
    normalizeRole,
    getRoleColor,
    getRoleIcon,
    getRoleLabel,
    getRoleFilterOptions
  } = useRoleNormalization();

  // State management
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Which inline panel (if any) is open below Quick Actions — only ONE at a time.
  const [activePanel, setActivePanel] = useState(null); // 'overview' | 'dbaudit' | 'voiceslots' | 'userManager' | reset | auditnull | null
  const togglePanel = (name) => setActivePanel((cur) => (cur === name ? null : name));
  const [userStats, setUserStats] = useState({
    total: 0,
    voiceActors: 0,
    bookCreators: 0,
    admins: 0,
    active: 0,
    inactive: 0
  });

  const loadUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      // Pass the current user from Redux to RealUserService
      const currentUser = userProfile?.data ? {
        userId: userProfile.data.userId || userProfile.data.id,
        ...userProfile.data
      } : null;

      console.log('UserManagement: Attempting to load users with current user:', currentUser?.userId);

      const userData = await realUserService.getAllRealData(currentUser);

      if (userData && userData.users) {
        const userList = Object.values(userData.users.all || userData.users);
        console.log('UserManagement: Successfully loaded users:', userList.length);
        setUsers(userList);
        calculateStats(userList);
      } else {
        throw new Error('No user data received');
      }
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(`Failed to load users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };


  const filterUsers = () => {
    let filtered = users;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply role filter using normalized roles
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => {
        const userRole = normalizeRole(user.primaryRole || user.role);
        return userRole === roleFilter;
      });
    }

    setFilteredUsers(filtered);
  };

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Filter users when search term or role filter changes
  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter]);

  const calculateStats = (userList) => {
    const stats = {
      total: userList.length,
      voiceActors: userList.filter(u => u.role === 'voiceActor' || u.primaryRole === 'voiceActor').length,
      bookCreators: userList.filter(u => u.role === 'bookCreator' || u.primaryRole === 'bookCreator').length,
      admins: userList.filter(u => u.role === 'admin' || u.primaryRole === 'admin').length,
      active: userList.filter(u => u.isActive !== false).length,
      inactive: userList.filter(u => u.isActive === false).length
    };
    setUserStats(stats);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  /* -------------------------------------------------------------------------- */
  /* UserNodeHealth — terse :Users system-node status + details (string form).  */
  /* Extracts the same /database-audit/health data, but renders the USERNODE    */
  /* status and its properties as compact text (not JSON) for a Statistic.Value.*/
  /* -------------------------------------------------------------------------- */
  const UserNodeHealth = () => {
    const dispatch = useDispatch();
    const { data: health, error: err } = useSelector(selectDbHealth);

    useEffect(() => { dispatch(fetchDbHealth()); }, [dispatch]);

    if (err) return <div style={{ fontSize: '0.9rem', color: '#c0392b' }}>Health error: {err}</div>;
    if (!health) return <div style={{ fontSize: '0.9rem', color: '#888' }}>Loading…</div>;

    const un = health.usersNode || {};
    const details = un.details && typeof un.details === 'object'
      ? Object.entries(un.details).map(([k, v]) => `${k}=${v}`).join(' · ')
      : '—';

    return (
      <div style={{ fontSize: '1rem', fontWeight: 400, textAlign: 'left', lineHeight: 1.4 }}>
        <div style={{ fontWeight: 600, color: un.exists ? '#2e7d32' : '#c0392b' }}>
          Users node: {un.exists ? 'OK' : 'MISSING'}
        </div>
        <div style={{ fontSize: '0.8rem', color: '#666' }}>Details: {details}</div>
      </div>
    );
  };

  /* -------------------------------------------------------------------------- */
  /* OverviewTallyPanel — node census (entity tallies)                          */
  /*  Quick Actions row. Self-contained fetch of /statistics.                   */
  /* -------------------------------------------------------------------------- */
  const OverviewTallyPanel = () => {
    const dispatch = useDispatch();
    const { data: stats, loading: busy, error: err } = useSelector(selectDbStatistics);

    useEffect(() => { dispatch(fetchDbStatistics()); }, [dispatch]);

    if (busy && !stats) return <Segment loading style={{ minHeight: 80 }} />;
    if (err) return <Message negative size="small"><Message.Header>Failed to load tallies</Message.Header><p>{err}</p></Message>;

    const rows = stats?.statistics || [];
    return (
      <Segment padded>
        <Header as="h3"><Icon name="database" /> System Entity Tallies</Header>
        {/* Scroll after ~25 rows; header stays pinned while the body scrolls. */}
        <div style={{ maxHeight: '780px', overflowY: 'auto' }}>
          <Table celled compact striped style={{ marginTop: 0 }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f9fafb' }}>Node Label</Table.HeaderCell>
                <Table.HeaderCell textAlign="right" style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f9fafb' }}>Count</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row, i) => (
                <Table.Row key={i}>
                  <Table.Cell>{Array.isArray(row.nodeType) ? row.nodeType.join(':') : row.nodeType}</Table.Cell>
                  <Table.Cell textAlign="right">{row.count}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </Segment>
    );
  };

  /* -------------------------------------------------------------------------- */
  /* ResetPanel — full-purge preview + guarded per-user delete. Reuses           */
  /* /database-audit/reset-preview and the hardened DELETE /delete/user/:id.      */
  /* Admins are always protected server-side; guests unless the toggle is off.    */
  /* -------------------------------------------------------------------------- */
  const ResetPanel = () => {
    const dispatch = useDispatch();
    const { data: report, loading: loadingR, error: fetchErr } = useSelector(selectResetPreview);
    const [protectGuests, setProtectGuests] = useState(true);
    const [msg, setMsg] = useState(null);
    const [delErr, setDelErr] = useState(null);           // per-user delete failure (one-off action)
    const [busy, setBusy] = useState(null);               // userId currently deleting
    const [confirmEmail, setConfirmEmail] = useState({});  // userId -> typed email
    const errR = fetchErr || delErr;

    useEffect(() => { dispatch(fetchResetPreview({ protectGuests })); }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleGuests = () => {
      const next = !protectGuests;
      setProtectGuests(next);
      dispatch(fetchResetPreview({ protectGuests: next }));
    };

    const deleteUser = async (u) => {
      if (confirmEmail[u.userId] !== u.email || busy) return;
      setBusy(u.userId); setMsg(null); setDelErr(null);
      try {
        // One-off action (not a database-audit endpoint) — stays a direct apiClient call.
        // 15-minute ceiling: the GDPR cascade routinely outlives the 30s default, and a timeout
        // here reports a failure for a delete that actually completed. (url, params, config).
        const resp = await apiClient.delete(`/delete/user/${u.userId}`, {}, { timeout: 900000 });
        const status = (resp.data?.status || '').toLowerCase();
        setMsg(`Deleted ${u.email} (${status || 'ok'}).`);
        dispatch(fetchResetPreview({ protectGuests }));
      } catch (e) {
        setDelErr(`Delete ${u.email} failed: ${e.response?.data?.errors?.join(', ') || e.message}`);
      } finally {
        setBusy(null);
      }
    };

    // --- search / role filter / paging over the WOULD-DELETE list (scales to hundreds) ---
    const del = useMemo(() => report?.delete || [], [report]);
    const [q, setQ] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [perPage, setPerPage] = useState(25);
    const [activePage, setActivePage] = useState(1);
    useEffect(() => { setActivePage(1); }, [q, roleFilter, perPage]); // reset to page 1 when the view narrows

    const roleOptions = useMemo(() => {
      const roles = Array.from(new Set(del.map((u) => u.role).filter(Boolean))).sort();
      return [{ key: 'all', value: 'all', text: 'All roles' }, ...roles.map((r) => ({ key: r, value: r, text: r }))];
    }, [del]);
    const perPageOptions = [
      { key: 25, value: 25, text: '25 / page' },
      { key: 50, value: 50, text: '50 / page' },
      { key: 100, value: 100, text: '100 / page' },
      { key: 'all', value: 9999, text: 'Show all' },
    ];
    const filtered = useMemo(() => {
      const term = q.trim().toLowerCase();
      return del.filter((u) => {
        const okQ = !term
          || (u.email || '').toLowerCase().includes(term)
          || (u.title || '').toLowerCase().includes(term)
          || (u.userId || '').toLowerCase().includes(term);
        return okQ && (roleFilter === 'all' || u.role === roleFilter);
      });
    }, [del, q, roleFilter]);
    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const page = Math.min(activePage, totalPages);
    const start = (page - 1) * perPage;
    const paged = filtered.slice(start, start + perPage);

    if (loadingR && !report) return <Segment loading style={{ minHeight: 80 }} />;
    if (errR && !report) return <Message negative size="small"><Message.Header>Failed to load reset preview</Message.Header><p>{errR}</p></Message>;

    return (
      <Segment padded>
        <Header as="h3"><Icon name="warning sign" color="red" /> Full Reset — preview &amp; per-user purge</Header>
        <p style={{ color: '#666' }}>
          Delete-User is admin-gated and the server protects admins (always) and guests (unless forced).
          A full reset is simply this operation applied to every WOULD-DELETE user.
        </p>
        {msg && <Message positive size="small">{msg}</Message>}
        {errR && <Message negative size="small">{errR}</Message>}

        <Checkbox label="Protect guest accounts" checked={protectGuests} onChange={toggleGuests} style={{ margin: '8px 0' }} />

        <Header as="h4"><Icon name="shield" color="green" /> Protected — {report?.protect?.length || 0} (never deleted)</Header>
        <List bulleted>
          {(report?.protect || []).map((u) => (
            <List.Item key={u.userId} style={{ color: '#2e7d32' }}>
              {u.reason} · {u.email} {u.title ? `· ${u.title}` : ''}
            </List.Item>
          ))}
        </List>

        <Header as="h4">
          <Icon name="trash" color="red" /> Would delete — {filtered.length}{filtered.length !== del.length ? ` of ${del.length}` : ''}
        </Header>
        {del.length === 0 ? <p>None.</p> : (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
              <Input icon="search" size="small" placeholder="Search email / title / id"
                value={q} onChange={(e) => setQ(e.target.value)} />
              <Dropdown selection compact options={roleOptions} value={roleFilter}
                onChange={(e, { value }) => setRoleFilter(value)} />
              <Dropdown selection compact options={perPageOptions} value={perPage}
                onChange={(e, { value }) => setPerPage(value)} />
              <span style={{ color: '#666', fontSize: '0.9em' }}>
                {filtered.length === 0 ? 'No matches'
                  : `Showing ${start + 1}–${Math.min(start + perPage, filtered.length)} of ${filtered.length}`}
              </span>
            </div>

            <div style={{ maxHeight: '780px', overflowY: 'auto' }}>
              <Table celled compact striped style={{ marginTop: 0 }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f9fafb' }}>Email</Table.HeaderCell>
                    <Table.HeaderCell style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f9fafb' }}>Role</Table.HeaderCell>
                    <Table.HeaderCell style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f9fafb' }}>Books</Table.HeaderCell>
                    <Table.HeaderCell style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f9fafb' }}>Type email to confirm</Table.HeaderCell>
                    <Table.HeaderCell style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f9fafb' }} />
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {paged.map((u) => (
                    <Table.Row key={u.userId}>
                      <Table.Cell>{u.email}</Table.Cell>
                      <Table.Cell>{u.role || '?'}</Table.Cell>
                      <Table.Cell>{u.books}{u.isActor ? ' · actor' : ''}</Table.Cell>
                      <Table.Cell>
                        <Input size="mini" placeholder={u.email}
                          value={confirmEmail[u.userId] || ''}
                          onChange={(e) => setConfirmEmail((m) => ({ ...m, [u.userId]: e.target.value }))} />
                      </Table.Cell>
                      <Table.Cell collapsing>
                        <Button negative size="mini"
                          disabled={confirmEmail[u.userId] !== u.email || busy === u.userId}
                          loading={busy === u.userId}
                          onClick={() => deleteUser(u)}>
                          Delete
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>

            {totalPages > 1 && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <Pagination
                  activePage={page}
                  totalPages={totalPages}
                  onPageChange={(e, d) => setActivePage(d.activePage)}
                  siblingRange={1}
                  boundaryRange={1}
                  size="mini"
                  ellipsisItem={{ content: '...', icon: 'ellipsis horizontal' }}
                  firstItem={{ content: '«', icon: 'angle double left' }}
                  lastItem={{ content: '»', icon: 'angle double right' }}
                  prevItem={{ content: '‹', icon: 'angle left' }}
                  nextItem={{ content: '›', icon: 'angle right' }}
                />
              </div>
            )}
          </>
        )}
      </Segment>
    );
  };

  /* -------------------------------------------------------------------------- */
  /* AuditPanel — durable deletion-audit trail (proof of erasure), newest first. */
  /* Reads /database-audit/deletion-audit. Rare events, so a simple list.         */
  /* -------------------------------------------------------------------------- */
  const AuditPanel = () => {
    const dispatch = useDispatch();
    const { data, loading: busy, error: err } = useSelector(selectDeletionAudit);

    const load = () => dispatch(fetchDeletionAudit({ limit: 100 }));
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (busy && !data) return <Segment loading style={{ minHeight: 80 }} />;
    if (err && !data) return <Message negative size="small"><Message.Header>Failed to load deletion audit</Message.Header><p>{err}</p></Message>;

    const rows = data?.records || [];
    const fmt = (ts) => { try { return new Date(ts).toLocaleString(); } catch (_) { return ts; } };
    return (
      <Segment padded>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Header as="h3" style={{ margin: 0 }}><Icon name="history" /> Deletion Audit — proof of erasure ({rows.length})</Header>
          <Button size="tiny" icon="refresh" content="Refresh" onClick={load} />
        </div>
        <p style={{ color: '#888', fontSize: '0.85em' }}>
          Append-only record written on each user deletion. Email is stored as a SHA-256 hash (PII-minimized).
        </p>
        {rows.length === 0 ? <p>No deletions recorded yet.</p> : (
          <div style={{ maxHeight: '780px', overflowY: 'auto' }}>
            <Table celled compact striped size="small" style={{ marginTop: 0 }}>
              <Table.Header>
                <Table.Row>
                  {['When', 'By admin', 'User ID', 'Email (sha256)', 'Nodes', 'S3', 'Status'].map((h) => (
                    <Table.HeaderCell key={h} style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f9fafb' }}>{h}</Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((r) => (
                  <Table.Row key={r.auditId} warning={(r.s3FilesFailed || 0) > 0 || r.status !== 'success'}>
                    <Table.Cell>{fmt(r.deletionTimestamp)}</Table.Cell>
                    <Table.Cell>{r.adminUser}</Table.Cell>
                    <Table.Cell style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.userId}</Table.Cell>
                    <Table.Cell style={{ fontFamily: 'monospace', fontSize: 11 }} title={r.emailHash}>{(r.emailHash || '').slice(0, 12)}…</Table.Cell>
                    <Table.Cell textAlign="right">{r.totalNodesDeleted}</Table.Cell>
                    <Table.Cell textAlign="right">{r.s3FilesDeleted}{(r.s3FilesFailed || 0) > 0 ? ` (${r.s3FilesFailed} failed)` : ''}</Table.Cell>
                    <Table.Cell>{r.status}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Segment>
    );
  };

  /* -------------------------------------------------------------------------- */
  /* S3ReconcilePanel — S3<->DB reconciliation + guarded dead-folder reclaim.     */
  /* Reads /database-audit/s3-reconcile; reclaim via /s3-reclaim, armed by typing */
  /* RECLAIM. Server refuses live users + system roots regardless.                */
  /* -------------------------------------------------------------------------- */
  const S3ReconcilePanel = () => {
    const dispatch = useDispatch();
    const { data: report, loading, error } = useSelector(selectS3Reconcile);
    const reclaimState = useSelector(selectS3Reclaim);
    const [confirm, setConfirm] = useState('');
    const [msg, setMsg] = useState(null);

    const humanBytes = (n) => {
      if (!n) return '0 B';
      if (n < 1024) return `${n} B`;
      const u = ['KB', 'MB', 'GB', 'TB']; let i = -1; let x = n;
      do { x /= 1024; i++; } while (x >= 1024 && i < u.length - 1);
      return `${x.toFixed(1)} ${u[i]}`;
    };

    const load = () => dispatch(fetchS3Reconcile({ deep: true }));
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const armed = confirm === 'RECLAIM';
    const busy = reclaimState.loading;
    const doReclaim = async (body, label) => {
      if (!armed || busy) return;
      setMsg(null);
      const res = await dispatch(reclaimS3({ ...body, confirm: 'RECLAIM' }));
      if (reclaimS3.fulfilled.match(res)) {
        setMsg(`${label}: deleted ${res.payload.totalObjects} object(s), ${humanBytes(res.payload.totalBytes)} reclaimed.`);
        setConfirm('');
        load();
      }
    };

    if (loading && !report) return <Segment loading style={{ minHeight: 80 }} />;
    if (error && !report) return <Message negative size="small"><Message.Header>Failed to reconcile S3</Message.Header><p>{error}</p></Message>;

    const dead = report?.deadUserFolders || [];
    return (
      <Segment padded>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Header as="h3" style={{ margin: 0 }}>
            <Icon name="cloud" /> S3 ⇄ DB Reconciliation <small style={{ color: '#888' }}>bucket {report?.bucket}</small>
          </Header>
          <Button size="tiny" icon="refresh" content="Refresh" onClick={load} loading={loading} />
        </div>
        {msg && <Message positive size="small">{msg}</Message>}
        {reclaimState.error && <Message negative size="small">{reclaimState.error}</Message>}

        <Header as="h4">Root census</Header>
        <List>
          {(report?.roots || []).map((p) => {
            const unknown = (report.unknownRoots || []).includes(p);
            return (
              <List.Item key={p} style={{ color: unknown ? '#c0392b' : '#2e7d32' }}>
                <Icon name={unknown ? 'exclamation triangle' : 'shield'} /> {p}{' '}
                {unknown ? '— UNKNOWN (investigate, do not auto-delete)' : '(system/known)'}
              </List.Item>
            );
          })}
        </List>

        <Header as="h4">
          <Icon name="trash" color="red" /> Dead user folders — {dead.length} ({humanBytes(report?.deadTotals?.bytes || 0)} reclaimable)
        </Header>
        {dead.length === 0 ? <p>None — every aai-users/ folder maps to a live user.</p> : (
          <div style={{ maxHeight: '780px', overflowY: 'auto' }}>
            <Table celled compact striped size="small" style={{ marginTop: 0 }}>
              <Table.Header>
                <Table.Row>
                  {['Prefix', 'Objects', 'Size', ''].map((h, i) => (
                    <Table.HeaderCell key={i} style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f9fafb' }}>{h}</Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {dead.map((d) => (
                  <Table.Row key={d.prefix}>
                    <Table.Cell style={{ fontFamily: 'monospace', fontSize: 11 }}>{d.prefix}</Table.Cell>
                    <Table.Cell>{d.count}</Table.Cell>
                    <Table.Cell>{humanBytes(d.bytes)}</Table.Cell>
                    <Table.Cell collapsing>
                      <Button negative size="mini" disabled={!armed || busy}
                        onClick={() => doReclaim({ prefixes: [d.prefix] }, d.prefix)}>Reclaim</Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}

        <Header as="h4">
          Live user folders: {report?.liveUserFolders?.length || 0} · DB users with no folder: {report?.dbUsersWithoutFolder?.length || 0}
        </Header>

        <Segment color="red" style={{ marginTop: 12 }}>
          <strong><Icon name="exclamation triangle" color="red" /> Danger zone.</strong> Type <code>RECLAIM</code> to enable deletion.
          Live users and system roots are refused server-side regardless.
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Input size="small" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="RECLAIM" />
            <Button negative disabled={!armed || busy || dead.length === 0} loading={busy}
              onClick={() => doReclaim({ deadUsers: true }, 'All dead folders')}>
              {`Reclaim all ${dead.length} dead folders`}
            </Button>
          </div>
        </Segment>
      </Segment>
    );
  };

  // Use getRoleLabel from hook instead of formatRole
  const formatRole = (role) => getRoleLabel(role) || 'Unknown';

  // Quick action cards for System Management
  const quickActions = [
    {
      icon: 'plus',
      img: userPuzzle,
      title: 'System Overview',
      description: 'System entity tallies',
      action: () => togglePanel('overview'), // toggle the tally panel below Quick Actions (exclusive)
      color: 'green'
    },
    {
      icon: 'database',
      img: lightening,
      title: 'Database Audit',
      description: 'Audit database health and find orphans',
      action: () => togglePanel('dbaudit'), // toggle the Database Audit panel below Quick Actions (exclusive)
      color: 'blue'
    },
    {
      icon: 'microphone',
      img: lightening,
      title: 'Voice Slots',
      description: 'ElevenLabs cap headroom, orphans & reclaim',
      action: () => togglePanel('voiceslots'), // toggle the Voice Slots panel below Quick Actions (exclusive)
      color: 'purple'
    },
    {
      icon: 'lock',
      img: lightening,
      title: 'Login Allowlist',
      description: 'Who may sign in — admit, disable, remove',
      action: () => togglePanel('allowlist'), // toggle the Login Allowlist panel (exclusive)
      color: 'red'
    },
    {
      icon: 'download',
      img: robotGearRocket,
      title: 'User Manager',
      description: 'User Management',
      action: () => navigate('/admin/user-management'), // full original User Management page (its own route)
      color: 'teal'
    },
    {
      icon: 'download',
      img: robotGearRocket,
      title: 'Reset',
      description: 'Reset System',
      action: () => togglePanel('reset'), // toggle the Reset (full-purge preview) panel below Quick Actions
      color: 'teal'
    },
    {
      icon: 'trash',
      img: warningIcon,
      title: 'Delete User',
      description: 'Safely remove user accounts',
      action: () => navigate('/admin/delete-user'),
      color: 'red'
    },
    {
      icon: 'history',
      img: lightening,
      title: 'Deletion Audit',
      description: 'Proof-of-erasure history',
      action: () => togglePanel('audit'), // toggle the Deletion Audit list below Quick Actions (exclusive)
      color: 'grey'
    },
    {
      icon: 'cloud',
      img: robotGearRocket,
      title: 'S3 Reconcile',
      description: 'S3 ⇄ DB dead-folder reclaim',
      action: () => togglePanel('s3'), // toggle the S3 reconcile/reclaim panel below Quick Actions (exclusive)
      color: 'orange'
    }
  ];

  // Get role filter options from the hook
  const roleFilterOptions = getRoleFilterOptions(true);

  return (
    <div className="landing-page admin-landing landing-left" >
      <div style={{ maxWidth: "1200px", marginTop: "30px", paddingLeft: "20px", paddingRight: "20px" }}>

        <Grid className="articleListWidth">
          <Grid.Row>
            <Grid.Column width={3}>
              {/* <div className="img-with-gradient"> */}
              <div className="">

                <Image src={userPuzzle} style={{ width: '140px', height: '140px' }} />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">System Management</span>
              <span className="blgListSubTitle">Comprehensive System Administration</span>
              <span className="blgListAuthor">Welcome Admin - {userName}</span>

              <p className="landing-subheading-text">
                Manage all system elements, including user accounts, roles, and permissions. View user statistics,
                search and filter users, and perform administrative actions.
              </p>
              <div style={{ textAlign: 'left', marginTop: '1em' }}>
                <Button
                  onClick={loadUsers}
                  size="large"
                  className="landing-goto-button"
                  loading={loading}
                  icon="refresh"
                  content="Refresh User Data"
                />
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="articleListWidth" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </div>

      <div style={{ maxWidth: "1200px", marginTop: '2em', marginBottom: '2em', paddingLeft: "20px", paddingRight: "20px" }}>
        {/* Error Message */}
        {error && (
          <Message negative>
            <Message.Header>Error Loading Users</Message.Header>
            <p>{error}</p>
            <Button onClick={loadUsers} primary>Retry</Button>
          </Message>
        )}

        {/* User Statistics */}
        <Grid>
          <Grid.Row>
            <Grid.Column width={6}>
              <Header as="h2">
                <img src={robotGearRocket} alt="" style={{ width: '90px' }} />
                <Header.Content style={{
                  fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                  fontWeight: '400',
                  fontSize: '2.5rem'
                }}>
                  Overview Statistics
                  <Header.Subheader>Current system metrics</Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>
            <Grid.Column width={10}>
              <Statistic.Group widths="five">
                <Statistic>
                  <Statistic.Value>
                    <div style={{
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                      fontWeight: '300',
                      fontSize: '3.0rem'
                    }}>
                      <img src={userPuzzle} alt="" style={{ width: '40px' }} /> {userStats.total}
                    </div>
                  </Statistic.Value>
                  <Statistic.Label>Total Users</Statistic.Label>
                </Statistic>

                <Statistic>
                  <Statistic.Value>
                    <div style={{
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                      fontWeight: '300',
                      fontSize: '3.0rem'
                    }}>
                      <img src={voiceActressBW} alt="" style={{ width: '40px' }} /> {userStats.voiceActors}
                    </div>
                  </Statistic.Value>
                  <Statistic.Label>Voice Actors</Statistic.Label>
                </Statistic>

                <Statistic>
                  <Statistic.Value>
                    <div style={{
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                      fontWeight: '300',
                      fontSize: '3.0rem'
                    }}>
                      <img src={bookStack} alt="" style={{ width: '40px' }} /> {userStats.bookCreators}
                    </div>
                  </Statistic.Value>
                  <Statistic.Label>Book Creators</Statistic.Label>
                </Statistic>

                <Statistic>
                  <Statistic.Value>
                    <div style={{
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                      fontWeight: '300',
                      fontSize: '3.0rem'
                    }}>
                      <img
                        src="/static/media/thodkin_librarian_futuristic_2a188bb5-2c71-4330-a561-ef70e9707b82.69a27af8cce99fd638a3.png"
                        alt="Admin Profile"
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          marginRight: '8px'
                        }}
                      /> {userStats.admins}
                    </div>
                  </Statistic.Value>
                  <Statistic.Label>Administrators</Statistic.Label>
                </Statistic>

                <Statistic>
                  <Statistic.Value>
                    <div style={{
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                      fontWeight: '300',
                      fontSize: '3.0rem'
                    }}>
                      <img src={activeUsersIcon} alt="" style={{ width: '90px' }} />{userStats.active}
                    </div>
                  </Statistic.Value>
                  <Statistic.Label>Active Users</Statistic.Label>
                </Statistic>
              </Statistic.Group>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid>
          <Grid.Row>
            <Grid.Column width={6}>
              <Header as="h2">
                <img src={robotGearRocket} alt="" style={{ width: '90px' }} />
                <Header.Content style={{
                  fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                  fontWeight: '400',
                  fontSize: '2.5rem'
                }}>
                  System Health
                  <Header.Subheader>Current system review</Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>
            <Grid.Column width={10}>
              <Statistic.Group widths="five">
                <Statistic style={{ alignItems: 'flex-start' }}>
                  <Statistic.Value>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      textAlign: 'left',
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif'
                    }}>
                      <img src={userPuzzle} alt="" style={{ width: '40px', flexShrink: 0 }} />
                      <UserNodeHealth />
                    </div>
                  </Statistic.Value>
                  <Statistic.Label >System Health</Statistic.Label>
                </Statistic>
              </Statistic.Group>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Divider />

        {/* Quick Actions */}
        <Header as="h2" dividing style={{ marginTop: '2em' }}>
          <img src={lightening} alt="" />
          <Header.Content>
            Quick Actions
            <Header.Subheader>Common system management tasks</Header.Subheader>
          </Header.Content>
        </Header>

        <Card.Group itemsPerRow={7} stackable>
          {quickActions.map((action, index) => (
            <Card
              key={index}
              onClick={action.action}
              raised
              style={{ cursor: 'pointer' }}
            >
              <Card.Content>
                <img src={action.img} alt="" style={{ height: '80px', display: 'block', margin: '0 auto 1em', color: '#3d6a87' }} />
                <Card.Header textAlign="center">{action.title}</Card.Header>
                <Card.Description textAlign="center">
                  {action.description}
                </Card.Description>
              </Card.Content>
            </Card>
          ))}
        </Card.Group>
        <Divider />
        {/* Quick-action inline panels */}
        {activePanel === 'overview' && <OverviewTallyPanel />}
        {activePanel === 'dbaudit' && <DatabaseAudit embedded />}
        {activePanel === 'voiceslots' && <VoiceSlotManager embedded />}
        {activePanel === 'allowlist' && <LoginAllowlistManager embedded />}
        {activePanel === 'reset' && <ResetPanel />}
        {activePanel === 'audit' && <AuditPanel />}
        {activePanel === 's3' && <S3ReconcilePanel />}
      </div>

      {AuthStdFooter()}
    </div>
  );
};

export default UserManagement;
